
import { message } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { ConfigGeneralContext } from '../../auth/context/ConfigGeneral/ConfigGeneralContext';
import { PRODUCT_OPTIONS } from '../../constants/filters';
import { useCardComponent } from '../../hooks/useCardComponent';
import { useStreamingAdmin } from '../../hooks/useStreamingAdmin';
import { STATUS_TYPE } from '../../services/API/statusType/status-type';
import { Loading } from '../Shared/Loading/Loading';
import MiniModal from '../Shared/Modal/MiniModal';
import ModalForm from '../Shared/ModalForm/ModalForm';
import UnifiedButtons from './components/UnifiedButtons';
import ModalLogsInformation from '../Shared/ModalLogsInformation/ModalLogsInformation';
import ModalColumnsInformation from '../Shared/ModalColumnsInformation/ModalColumnsInformation';
import { getStreamColumns } from '../../services/API/CardComponentApi';

const CardComponent = ({ dashboard, project, client, index, name, type, path, paths, isPending, onHandleDataStreamRefecth, streamingStatus }) => {
 //Custom Hooks
 const {
  setEnabledStreaming,
  setToken,
  setDasboard,
  triggerGetLogsStreaming
 } = useCardComponent();

 const {
  triggerCreateStream,
  triggerDeleteStream,
  triggerStartStream,
  triggerUpdateStreamPath,
  triggerUpdateStreamName,
  triggerResetStream
 } = useStreamingAdmin();

 const [formData, setFormData] = useState({
  streamingType: type || '',
  streamingName: name || '',
  streamingPath: path || ''
 });
 const [isModalVisible, setIsModalVisible] = useState(false);
 const [isStartEnabled, setIsStartEnabled] = useState(false);
 const [modalConfig, setModalConfig] = useState({});
 const [isEditable, setIsEditable] = useState(isPending);
 const [pendingType, setPendingType] = useState(isPending);
 const [isLoading, setIsLoading] = useState(false);
 const [isLogsModalVisible, setIsLogsModalVisible] = useState(false);
 const [isColumnsModalVisible, setIsColumnsModalVisible] = useState(false);
 const productTypesMap = {
  "Drill BI": ["os", "tbd"],
  "Drilling Optimization": ["default"],
 };


 useEffect(() => {
  if (dashboard) {
   setEnabledStreaming(true);
   setToken('session'); // only part of the query key: the API key is added by nginx
   setDasboard(dashboard);
  }
 }, [dashboard]);

 const allFieldsFilled = () => {
  return (
   formData.streamingType &&
   formData.streamingName &&
   formData.streamingPath
  );
 };

 const handleInputChange = (e) => {
  const { id, value } = e.target;
  setFormData({
   ...formData,
   [id]: value
  });
 }

 useEffect(() => {
  if (!path) setPendingType(type);
  else setPendingType(null);
 }, [path, type]);

 const handleCreate = async () => {
  if (!allFieldsFilled()) {
   showModal("Missing Information", "All fields must be filled to create the streaming.", [
    { label: "OK", onClick: () => setIsModalVisible(false), className: "primary" },
   ]);
   return;
  }

  const { streamingType, streamingName, streamingPath } = formData;
  const product = PRODUCT_OPTIONS.find((x) => x.label === dashboard || x.value === dashboard).value;

  try {
   const response = await triggerCreateStream({
    product,
    client,
    project,
    streamType: streamingType,
    wellName: streamingName,
    streamPath: streamingPath,
   });

   if (response.statusType === STATUS_TYPE.SUCCESS) {
    showModal("Success", response.statusMessage || "Streaming created successfully!", [
     { label: "OK", onClick: () => setIsModalVisible(false), className: "primary" },
    ]);
    setIsStartEnabled(true);
   } else {
    showModal("Error", response.statusMessage || "Failed to create streaming.", [
     { label: "OK", onClick: () => setIsModalVisible(false), className: "danger" },
    ]);
   }
  } catch (error) {
   showModal("Error", "Failed to create streaming. Please try again.", [
    { label: "OK", onClick: () => setIsModalVisible(false), className: "danger" },
   ]);
  }
 };

 const handleStart = async () => {
  const { streamingType, streamingName } = formData;
  const product = PRODUCT_OPTIONS.find((x) => x.label === dashboard || x.value === dashboard).value;

  try {
   const response = await triggerStartStream({
    product,
    client,
    project,
    streamType: streamingType,
    wellName: streamingName,
   });

   if (response.statusType === STATUS_TYPE.SUCCESS) {
    showModal("Success", response.statusMessage || "Streaming started successfully!", [
     {
      label: "OK",
      onClick: () => {
       setIsModalVisible(false);
       onHandleDataStreamRefecth();
      },
      className: "primary",
     },
    ]);
   } else {
    showModal("Error", response.statusMessage || "Failed to start streaming.", [
     { label: "OK", onClick: () => setIsModalVisible(false), className: "danger" },
    ]);
   }
  } catch (error) {
   showModal("Error", "Failed to start streaming. Please try again.", [
    { label: "OK", onClick: () => setIsModalVisible(false), className: "danger" },
   ]);
  }
 };

 const showModal = (title, message, actions) => {
  setModalConfig({ title, message, actions });
  setIsModalVisible(true);
 };

 const handleLog = () => {
  setIsLogsModalVisible(true);
 };

 // Stream type config vs. table columns, for each stream type of this well
 const handleFetchColumns = async () => {
  const { streamingName } = formData;
  const product = PRODUCT_OPTIONS.find(
   (x) => x.label === dashboard || x.value === dashboard
  ).value;
  const results = {};
  for (const streamType of productTypesMap[dashboard] || []) {
   try {
    const response = await getStreamColumns(product, client, project, streamType, streamingName);
    results[streamType] = response?.statusType === "SUCCESS"
     ? { data: response.data }
     : { error: response?.statusMessage || "Could not get the columns." };
   } catch (error) {
    results[streamType] = { error: error.message || "Could not get the columns." };
   }
  }
  return results;
 };

 const handleFetchLogs = async (logType) => {
  const { streamingName } = formData;
  const product = PRODUCT_OPTIONS.find(
   (x) => x.label === dashboard || x.value === dashboard
  ).value;
  const productTypes = productTypesMap[dashboard] || []; // Obtener los tipos de producto según el dashboard
  let allLogs = []; // Acumular los logs obtenidos

  try {
   for (const type of productTypes) {
    const response = await triggerGetLogsStreaming(
     product,
     client,
     type,
     project,
     streamingName,
     logType
    );

    // Manejar respuestas en texto
    if (response) {
     allLogs.push({
      type,
      logs: response, // Almacenar el texto completo
     });
    } else {
     showErrorModal(
      `Failed to fetch logs for type "${type}": Invalid responses.`
     );
    }
   }

   return allLogs; // Retorna todos los logs acumulados
  } catch (error) {
   showErrorModal(
    `Error fetching logs: ${error.message || "Unknown error"}`
   );
   throw error; // Propaga el error para manejarlo en el modal si es necesario
  }
 };


 const handleDelete = () => {
  // Obtener los tipos del producto actual
  const productTypes = productTypesMap[dashboard] || [];

  showModal(
   "Confirm Deletion",
   `Are you sure you want to delete all streaming data for "${formData.streamingName}"?`,
   [
    { label: "Yes", onClick: () => confirmDelete(productTypes), className: "primary" },
    { label: "No", onClick: () => setIsModalVisible(false), className: "danger" },
   ]
  );
 };

 const confirmDelete = async (types) => {
  const { streamingName } = formData;
  const product = PRODUCT_OPTIONS.find((x) => x.label === dashboard || x.value === dashboard).value;

  try {
   const deletePromises = types.map((type) =>
    triggerDeleteStream({
     product,
     client,
     project,
     streamType: type,
     wellName: streamingName,
    })
   );

   // Ejecutar todas las promesas en paralelo
   const results = await Promise.all(deletePromises);

   // Verificar los resultados
   const allSuccess = results.every((res) => res.statusType === STATUS_TYPE.SUCCESS);

   if (allSuccess) {
    showModal("Success", "All streaming data deleted successfully!", [
     {
      label: "OK",
      onClick: () => {
       setIsModalVisible(false); // Cerrar el modal
       onHandleDataStreamRefecth(); // Refrescar los datos después
      },
      className: "primary",
     },
    ]);
   } else {
    const errorMessages = results
     .filter((res) => res.statusType !== STATUS_TYPE.SUCCESS)
     .map((res) => res.statusMessage || "Unknown error")
     .join(", ");

    showModal("Error", `Failed to delete some streams: ${errorMessages}`, [
     { label: "OK", onClick: () => setIsModalVisible(false), className: "danger" },
    ]);
   }
  } catch (error) {
   showModal("Error", "Failed to delete streaming data. Please try again.", [
    { label: "OK", onClick: () => setIsModalVisible(false), className: "danger" },
   ]);
  }
 };

 const handleEdit = () => {
  // Obtener los tipos correspondientes
  const productTypes = productTypesMap[dashboard] || [];

  // Configurar valores iniciales para el modal
  const initialPaths = productTypes.reduce((acc, type) => {
   acc[type] = paths?.[type] || ""; // Obtener los paths existentes o inicializar en vacío
   return acc;
  }, {});

  const initialFormData = {
   streamingName: name,
   ...initialPaths,
  };

  // Configurar el modal con los valores iniciales
  setModalConfig({
   isVisible: true,
   isEditable: true,
   initialValues: initialFormData, // Enviamos initialFormData aquí
   pathsConfig: productTypes, // Tipos de paths dinámicos
   onSave: (updatedData) => handleSaveEdit(updatedData, productTypes, initialFormData), // Pasamos initialFormData aquí
   onClose: () => setModalConfig((prev) => ({ ...prev, isVisible: false })),
  });
 };


 const handleSaveEdit = async (updatedData, productTypes, initialFormData) => {
  const { streamingName: newName, ...updatedPaths } = updatedData;

  console.log("Initial Data:", initialFormData);
  console.log("Updated Data:", updatedData);

  // Validar si hubo cambios
  const noChanges = initialFormData.streamingName === newName &&
   productTypes.every((type) => initialFormData[type] === updatedPaths[type]);

  if (noChanges) {
   console.log("No changes detected. Closing modal...");
   setIsModalVisible(false);
   return;
  }

  // Configurar MiniModal para confirmar los cambios
  setModalConfig({
   title: "Confirm Changes",
   message: "You have made changes to the streaming configuration. Do you want to apply them?",
   actions: [
    {
     label: "Yes",
     onClick: async () => {
      setIsModalVisible(false);
      await applyChanges(updatedData, productTypes, initialFormData);
     },
     className: "primary",
    },
    {
     label: "No",
     onClick: () => {
      setIsModalVisible(false); // Solo cierra el MiniModal
      console.log("User canceled the changes.");
     },
     className: "danger",
    },
   ],
  });

  setIsModalVisible(true); // Mostrar MiniModal
 };

 // Lógica centralizada para aplicar cambios
 const applyChanges = async (updatedData, productTypes, initialFormData) => {
  setIsLoading(true);
  const { streamingName: newName, ...updatedPaths } = updatedData;
  const product = PRODUCT_OPTIONS.find((x) => x.label === dashboard || x.value === dashboard).value;

  try {
   let hasError = false; // Flag para identificar si hay algún error

   // Cambiar solo el nombre
   if (
    initialFormData.streamingName !== newName &&
    productTypes.every((type) => initialFormData[type] === updatedPaths[type])
   ) {
    console.log("Changing only the name...");
    for (const type of productTypes) {
     const response = await triggerUpdateStreamName({
      product,
      client,
      project,
      streamType: type,
      wellName: initialFormData.streamingName,
      newStreamName: newName,
     });

     if (response.statusType !== STATUS_TYPE.SUCCESS) {
      hasError = true;
      showErrorModal(`Failed to update name for type "${type}": ${response.statusMessage}`);
      break; // Detener la ejecución si hay un error
     }
    }

    if (!hasError) {
     showSuccessModal("Name updated successfully!");
    }
    return;
   }

   // Cambiar paths
   if (initialFormData.streamingName === newName) {
    console.log("Changing only the paths...");
    for (const type of productTypes) {
     if (initialFormData[type] !== updatedPaths[type]) {
      const response = await triggerUpdateStreamPath({
       product,
       client,
       project,
       streamType: type,
       wellName: newName,
       newStreamPath: updatedPaths[type],
      });

      if (response.statusType !== STATUS_TYPE.SUCCESS) {
       hasError = true;
       showErrorModal(`Failed to update path for type "${type}": ${response.statusMessage}`);
       break; // Detener la ejecución si hay un error
      }
     }
    }

    if (!hasError) {
     showSuccessModal("Path(s) updated successfully!");
    }
    return;
   }

   // Cambiar nombre y paths
   if (
    initialFormData.streamingName !== newName ||
    productTypes.some((type) => initialFormData[type] !== updatedPaths[type])
   ) {
    console.log("Changing name and paths...");
    for (const type of productTypes) {
     if (initialFormData.streamingName !== newName) {
      const nameResponse = await triggerUpdateStreamName({
       product,
       client,
       project,
       streamType: type,
       wellName: initialFormData.streamingName,
       newStreamName: newName,
      });

      if (nameResponse.statusType !== STATUS_TYPE.SUCCESS) {
       hasError = true;
       showErrorModal(`Failed to update name for type "${type}": ${nameResponse.statusMessage}`);
       break; // Detener la ejecución si hay un error
      }
     }

     if (initialFormData[type] !== updatedPaths[type]) {
      const pathResponse = await triggerUpdateStreamPath({
       product,
       client,
       project,
       streamType: type,
       wellName: newName,
       newStreamPath: updatedPaths[type],
      });

      if (pathResponse.statusType !== STATUS_TYPE.SUCCESS) {
       hasError = true;
       showErrorModal(`Failed to update path for type "${type}": ${pathResponse.statusMessage}`);
       break; // Detener la ejecución si hay un error
      }
     }
    }

    if (!hasError) {
     showSuccessModal("Name and paths updated successfully!");
    }
    return;
   }

   console.log("No changes detected.");
  } catch (error) {
   console.error("Error during editing process:", error.message);
   showErrorModal("An unexpected error occurred while updating streaming data. Please try again.");
  }
 };



 // MiniModal para éxito
 const showSuccessModal = (message, refetch = true) => {
  setIsLoading(false);
  setModalConfig({
   title: "Success",
   message,
   actions: [
    {
     label: "OK",
     onClick: () => {
      setIsModalVisible(false);
      if (refetch) {
       onHandleDataStreamRefecth(); // Refresca los datos
      }
     },
     className: "primary",
    },
   ],
  });
  setIsModalVisible(true);
 };


 const handleReset = () => {
  // Obtener los tipos de streams según el producto
  const productTypes = productTypesMap[dashboard] || [];
  const product = PRODUCT_OPTIONS.find((x) => x.label === dashboard || x.value === dashboard).value;

  // Mostrar MiniModal de confirmación
  setModalConfig({
   title: "Confirm Reset",
   message: `Are you sure you want to reset the streams for "${name}"?`,
   actions: [
    {
     label: "Yes",
     onClick: () => {
      confirmReset(product, productTypes)
      setIsLoading(true);
      setIsModalVisible(false);
     },
     className: "primary",
    },
    {
     label: "No",
     onClick: () => setIsModalVisible(false),
     className: "danger",
    },
   ],
  });
  setIsModalVisible(true);
 };

 const confirmReset = async (product, productTypes) => {
  try {
   // Realizar el reset para cada tipo de stream
   const resetPromises = productTypes.map((type) =>
    triggerResetStream({
     product,
     client,
     project,
     streamType: type,
     wellName: name,
    })
   );

   // Ejecutar todas las promesas
   const results = await Promise.all(resetPromises);

   // Validar si todas las solicitudes fueron exitosas
   const allSuccess = results.every((res) => res.statusType === STATUS_TYPE.SUCCESS);

   if (allSuccess) {
    showSuccessModal("Streams reset successfully!", false);
   } else {
    const errorMessages = results
     .filter((res) => res.statusType !== STATUS_TYPE.SUCCESS)
     .map((res) => res.statusMessage || "Unknown error")
     .join(", ");

    showErrorModal(`Failed to reset some streams: ${errorMessages}`);
   }
  } catch (error) {
   console.error("Error during reset process:", error.message);
   showErrorModal("Failed to reset streams. Please try again.");
  }
 };


 const showErrorModal = (message) => {
  setModalConfig({
   title: "Error",
   message,
   actions: [
    {
     label: "OK",
     onClick: () => {
      setIsModalVisible(false)
      setIsLoading(false);
     }, // Solo cierra el MiniModal
     className: "danger",
    },
   ],
  });
  setIsModalVisible(true);
 };

 const renderButtons = () => {
  if (index === 0) {
   // Ambas tarjetas completas, mostrar un único módulo
   return (
    <UnifiedButtons
     isPending={false}
     isStartEnabled={true}
     onResetDB={handleReset}
     onLog={handleLog}
     onColumns={() => setIsColumnsModalVisible(true)}
     onEdit={handleEdit}
     onDelete={handleDelete}
    />
   );
  }

  // Si alguna está pendiente, mostrar Create y Start solo para esa
  if (isPending) {
   return (
    <UnifiedButtons
     isPending={true}
     isStartEnabled={false}
     onCreate={handleCreate}
     onStart={handleStart}
    />
   );
  }

  // Caso por defecto, sin botones
  return null;
 };


 return (
  <>
   {/* IsLoading */}
   {isLoading && <Loading />}

   {/* MiniModal */}
   {isModalVisible && <MiniModal {...modalConfig} />}

   {/* ModalLLogsInformation*/}
   {isLogsModalVisible && (
    <ModalLogsInformation
     isVisible={isLogsModalVisible}
     onClose={() => setIsLogsModalVisible(false)}
     onFetchLogs={handleFetchLogs}
    />
   )}
   {/* ModalColumnsInformation */}
   {isColumnsModalVisible && (
    <ModalColumnsInformation
     isVisible={isColumnsModalVisible}
     onClose={() => setIsColumnsModalVisible(false)}
     onFetchColumns={handleFetchColumns}
     types={productTypesMap[dashboard] || []}
    />
   )}
   {/* ModalForm */}
   {modalConfig.isVisible && (
    <ModalForm
     isVisible={modalConfig.isVisible}
     onClose={modalConfig.onClose}
     onSave={modalConfig.onSave}
     streamingName={modalConfig.initialValues.streamingName}
     pathsConfig={modalConfig.pathsConfig}
     isEditable={modalConfig.isEditable}
     initialFormData={modalConfig.initialValues}
    />
   )}

   <div className="bg-nc-gray p-6 rounded-lg shadow-lg text-white mt-5 w-[50%] ml-auto mr-auto">
    <div className="grid grid-cols-4 gap-x-4 items-start">
     {/* Streaming Type */}
     <div className="flex flex-col col-span-1 gap-y-2">
      <label htmlFor="streamingType" className="font-bold mb-1">
       Streaming Type
      </label>
      <input
       type="text"
       value={formData.streamingType}
       readOnly
       className="bg-gray-700 rounded-lg p-2 text-white"
      />
      {streamingStatus?.[type] && (
       <span
        className={`inline-flex items-center justify-center text-base font-semibold text-white py-1 px-4 rounded-full`}
        style={{
         backgroundColor: streamingStatus[type].color,
         position: "relative",
         top: "40px",
         maxWidth: "fit-content",
        }}
       >
        {streamingStatus[type].name}
       </span>
      )}

     </div>

     {/* Streaming Name + Path */}
     <div className="flex flex-col col-span-2 gap-y-2">
      <label htmlFor="streamingName" className="font-bold mb-1">
       Streaming Name
      </label>
      <input
       type="text"
       id="streamingName"
       value={formData.streamingName}
       onChange={handleInputChange}
       readOnly={!isEditable}
       className={`bg-gray-700 rounded-lg p-2 text-white ${isEditable ? "border-2 border-blue-500" : ""}`}
      />
      <label htmlFor="streamingPath" className="font-bold mb-1">
       Streaming Path
      </label>
      <input
       type="text"
       id="streamingPath"
       value={formData.streamingPath}
       onChange={handleInputChange}
       readOnly={!isEditable}
       className={`bg-gray-700 rounded-lg p-2 text-white ${isEditable ? "border-2 border-blue-500" : ""}`}
      />
     </div>
     {renderButtons()}
    </div>
   </div>
  </>
 );
};

export default CardComponent;